import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogTitle, Input, RadioGroup, Select } from '@/components/ui';
import type { AdminPaymentDetail, RefundReason } from '@/lib/api-admin/types';
import { KR_BANKS, accountDigits } from '@/lib/domain/krBanks';
import { formatPaymentMinorAmount, minorToMajorString } from '@/features/orders/lib/chargeMoney';
import {
  MANUAL_REFUND_METHODS,
  REFUND_REASON_OPTIONS,
  createManualRefundFormSchema,
  type ManualRefundFormValues,
  type RefundAccountInput,
} from '../lib/paymentSchema';
import { legacyPgLookupNo, type LegacyPgInfo } from '../lib/refundFlags';
import type { ManualRefundRequestBody } from '../hooks/usePaymentActionMutation';
import {
  EMPTY_REFUND_SCOPE,
  productRefundDefaultAmount,
  refundScopeBody,
  refundScopeError,
  type RefundScopeItem,
  type RefundScopeState,
} from '../lib/refundScope';
import { RefundScopeFields } from './RefundScopeFields';

export interface ManualRefundModalProps {
  payment: AdminPaymentDetail;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  /**
   * true = 이관 LEGACY·수기주문(PG 결제키 없음) — 방법을 고를 수 있다.
   * false = Toss 가상계좌 — 계좌이체로 고정(그 외는 BE 409 PAYMENT_METHOD_NOT_SUPPORTED).
   */
  manualRefundOnly: boolean;
  /**
   * QA-438 — 고를 수 있는 방법. 토스 계좌이체·가상계좌는 BE 가 원 PG 취소·계좌이체만 받는다(기타는 409) → 둘만.
   * 미지정 = 전부(이관·수기주문).
   */
  allowedMethods?: ReadonlyArray<(typeof MANUAL_REFUND_METHODS)[number]>;
  /** 이관 결제의 고도몰 원 PG 정보 — "원 PG 취소" 안내에 PG 이름·거래 번호를 쓴다. */
  legacyPg?: LegacyPgInfo | null;
  /** 클레임에서 진입했을 때 고객이 남긴 환불 계좌(계좌이체 기본값). */
  prefillRefundAccount?: RefundAccountInput | null;
  /** 클레임에서 진입했으면 그 클레임 — 기록하면 클레임이 환불 완료로 바뀐다. */
  claimId?: string | null;
  /** 기본 금액(청구통화 major) — 클레임의 예상 환불액. 남은 환불 가능액을 넘으면 남은 금액으로 둔다. */
  defaultAmount?: number | null;
  /** QA-625 환불 구분용 주문 상품(환불 가능 수량 1개 이상). 넘기면(클레임이 아닐 때) 환불 구분을 고르게 한다. */
  scopeItems?: ReadonlyArray<RefundScopeItem>;
  /** idempotencyKey = 이 모달을 연 동안 유지되는 키(재시도해도 같다). */
  onSubmit: (body: ManualRefundRequestBody, idempotencyKey: string) => void;
  onCancel: () => void;
}

type PaymentWithCharge = AdminPaymentDetail & { chargeRemainingRefundableAmount?: string | null };

const DECIMAL_PLACES: Record<string, number> = { KRW: 0, USD: 2, JPY: 0, CNY: 2 };

/** datetime-local("2026-09-30T14:00") → ISO. 비면 null(기록 시각). */
const localToIso = (v: string | undefined): string | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** BE Idempotency-Key 는 16~64자 — UUID(36자). */
const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `manual-refund-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * 수기 환불 기록(be-payment V20, `POST /payments/{id}/manual-refund`).
 * PG 를 부르지 않는다 — 운영자가 에버라인 밖에서 처리한 환불(원 PG 관리자 취소·고객 계좌 이체 등)을 기록만 한다.
 * Toss 가상계좌 결제에서는 [환불](Toss 송금)과 나란히 쓰이며 계좌이체로 고정된다.
 * 기록하면 환불 완료로 처리되고 되돌릴 수 없어, 보내기 전에 한 번 더 확인한다.
 */
export const ManualRefundModal = ({
  payment,
  isOpen,
  isSubmitting,
  errorMessage,
  manualRefundOnly,
  allowedMethods,
  legacyPg = null,
  prefillRefundAccount = null,
  claimId = null,
  defaultAmount = null,
  scopeItems,
  onSubmit,
  onCancel,
}: ManualRefundModalProps) => {
  const { t } = useTranslation('payments');
  const remainingMinor = payment.remainingRefundableAmount ?? payment.amount;
  const dp = DECIMAL_PLACES[payment.currencyCode] ?? 0;
  const remainingMajorStr =
    (payment as PaymentWithCharge).chargeRemainingRefundableAmount ??
    minorToMajorString(remainingMinor, payment.currencyCode);
  const remaining = remainingMajorStr != null ? Number(remainingMajorStr) : remainingMinor;
  const initialAmount = defaultAmount != null && defaultAmount > 0 ? Math.min(defaultAmount, remaining) : remaining;

  // 모달을 열 때마다 새 키 — 열린 동안의 재시도는 같은 키라 BE 가 한 번만 기록한다.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  useEffect(() => {
    if (isOpen) setIdempotencyKey(newIdempotencyKey());
  }, [isOpen]);

  const schema = useMemo(
    () => createManualRefundFormSchema({ remainingRefundableAmount: remaining, maxDecimalPlaces: dp }),
    [remaining, dp],
  );
  const defaults = useMemo<ManualRefundFormValues>(
    () => ({
      amount: initialAmount,
      reason: 'CHANGE_OF_MIND',
      detail: '',
      method: manualRefundOnly ? 'ORIGINAL_PG' : 'BANK_TRANSFER',
      refundAccount: prefillRefundAccount ?? { bankCode: '', accountNumber: '', holderName: '' },
      processedAt: '',
    }),
    [initialAmount, manualRefundOnly, prefillRefundAccount],
  );

  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm<ManualRefundFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const showScope = scopeItems !== undefined && !claimId;
  const [scope, setScope] = useState<RefundScopeState>(EMPTY_REFUND_SCOPE);
  const [scopeTouched, setScopeTouched] = useState(false);
  const scopeErr = showScope && scopeTouched ? refundScopeError(scope) : null;
  // 상품을 고르면 청구통화 단가 × 수량을 기본 금액으로(남은 환불 가능액까지). 운영자가 고칠 수 있다.
  const changeScope = (next: RefundScopeState) => {
    setScope(next);
    if (next.scope !== 'PRODUCT') return;
    const amount = productRefundDefaultAmount(scopeItems ?? [], next, payment.currencyCode, dp);
    if (amount != null) setValue('amount', Math.min(amount, remaining));
  };

  // 열 때마다 초기값으로 — 남은 환불 가능액은 직전 처리 결과를 다시 불러온 값이다.
  useEffect(() => {
    reset(defaults);
    setScope(EMPTY_REFUND_SCOPE);
    setScopeTouched(false);
  }, [isOpen, defaults, reset]);

  const method = watch('method');
  const bankOptions = useMemo(() => {
    const code = prefillRefundAccount?.bankCode;
    return code && !KR_BANKS.some((b) => b.code === code)
      ? [...KR_BANKS, { code, name: t('payments.refundModal.bankUnknown', { code, defaultValue: '코드 {{code}}' }) }]
      : KR_BANKS;
  }, [prefillRefundAccount, t]);

  // 방법별 안내 — 원 PG 취소면 어느 PG 의 어느 거래를 취소해야 하는지 짚는다.
  const guide =
    method === 'ORIGINAL_PG'
      ? legacyPg && (legacyPg.pgLabel || legacyPgLookupNo(legacyPg))
        ? t('payments.manualRefund.guideOriginalPg', {
            pg: legacyPg.pgLabel ?? legacyPg.pgName ?? 'PG',
            no: legacyPgLookupNo(legacyPg) ?? '—',
            defaultValue: '{{pg}} 관리자 화면에서 PG 거래 번호 {{no}} 로 취소한 뒤 기록하세요.',
          })
        : t('payments.manualRefund.guideOriginalPgGeneric', '원 결제 PG 관리자 화면에서 취소한 뒤 기록하세요.')
      : method === 'BANK_TRANSFER'
        ? t('payments.manualRefund.guideTransfer', '운영자가 고객 계좌로 직접 이체한 뒤 기록하세요. Toss 송금은 일어나지 않습니다.')
        : t('payments.manualRefund.guideOther', '에버라인 밖에서 처리한 내용을 상세 사유에 남겨 주세요. Toss 송금은 일어나지 않습니다.');

  const submit = handleSubmit((v) => {
    if (showScope && refundScopeError(scope)) {
      setScopeTouched(true);
      return;
    }
    const ok = window.confirm(
      t('payments.manualRefund.confirmPrompt', '실제 환불을 마친 뒤 기록해 주세요. 기록하면 환불 완료로 처리되고 되돌릴 수 없습니다.'),
    );
    if (!ok) return;
    const account = v.method === 'BANK_TRANSFER' && v.refundAccount ? v.refundAccount : null;
    onSubmit(
      {
        amount: v.amount,
        reason: v.reason as RefundReason,
        detail: v.detail && v.detail.trim() !== '' ? v.detail.trim() : null,
        method: v.method,
        refundAccount: account
          ? {
              bankCode: account.bankCode.trim(),
              accountNumber: accountDigits(account.accountNumber),
              holderName: account.holderName.trim(),
            }
          : null,
        processedAt: localToIso(v.processedAt),
        claimId,
        ...(showScope ? refundScopeBody(scope) : {}),
      },
      idempotencyKey,
    );
  });

  const amountError =
    errors.amount?.message === 'amount.exceeds_refundable'
      ? t('payments.refundModal.amountExceedsRefundable', {
          remaining: formatPaymentMinorAmount(remainingMinor, payment.currencyCode),
        })
      : errors.amount
        ? t('payments.refundModal.amountInvalid')
        : null;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="manual-refund-modal">
        <DialogTitle>
          {manualRefundOnly
            ? t('payments.manualRefund.title', '수기 환불 기록')
            : t('payments.manualRefund.titleTransfer', '수기 환불 기록(계좌이체)')}
        </DialogTitle>
        <p
          className="mt-2 rounded px-2 py-1.5 text-[12px]"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
          data-testid="manual-refund-notice"
        >
          {guide}
        </p>
        <p className="mt-2 text-sm text-[color:var(--content-muted)]">
          {t('payments.refundModal.refundable')}:{' '}
          <span className="tabular-nums text-[color:var(--content-strong)]">
            {formatPaymentMinorAmount(remainingMinor, payment.currencyCode)}
          </span>
        </p>

        <form onSubmit={submit} className="mt-3 space-y-4" noValidate>
          {showScope ? (
            <RefundScopeFields
              idPrefix="manual-refund"
              items={scopeItems ?? []}
              state={scope}
              onChange={changeScope}
              error={scopeErr}
            />
          ) : null}

          <div className="flex flex-col">
            <label htmlFor="manual-refund-amount" className="text-sm text-[color:var(--content-muted)]">
              {t('payments.refundModal.amount')}
            </label>
            <Input
              id="manual-refund-amount"
              type="number"
              step={dp === 2 ? '0.01' : '1'}
              {...register('amount', { valueAsNumber: true })}
            />
            {amountError ? (
              <p className="mt-1 text-xs" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                {amountError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <span id="manual-refund-method-label" className="text-sm text-[color:var(--content-muted)]">
              {t('payments.manualRefund.method', '환불 방법')}
            </span>
            {manualRefundOnly ? (
              <Controller
                control={control}
                name="method"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    options={MANUAL_REFUND_METHODS.filter((m) => !allowedMethods || allowedMethods.includes(m)).map((m) => ({
                      value: m,
                      label: t(`payments.manualRefund.methods.${m}`, {
                        defaultValue: { ORIGINAL_PG: '원 PG 취소', BANK_TRANSFER: '계좌이체', OTHER: '기타' }[m],
                      }),
                    }))}
                    orientation="horizontal"
                    aria-labelledby="manual-refund-method-label"
                    data-testid="manual-refund-method"
                  />
                )}
              />
            ) : (
              // Toss 가상계좌는 계좌이체만 — 고를 것이 없으니 고정 표시.
              <span className="text-sm text-[color:var(--content-strong)]" data-testid="manual-refund-method-fixed">
                {t('payments.manualRefund.methods.BANK_TRANSFER', '계좌이체')}
              </span>
            )}
          </div>

          {method === 'BANK_TRANSFER' ? (
            <fieldset className="rounded-md border p-3" style={{ borderColor: 'var(--surface-1)' }}>
              <legend className="px-1 text-sm font-medium text-[color:var(--content-strong)]">
                {t('payments.manualRefund.transferAccount', '이체한 고객 계좌')}
              </legend>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <Select aria-label={t('payments.refundModal.bankCode')} data-testid="manual-refund-bank" {...register('refundAccount.bankCode')}>
                  <option value="">{t('payments.refundModal.bankSelect', '은행 선택')}</option>
                  {bankOptions.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </Select>
                <Input
                  inputMode="numeric"
                  placeholder={t('payments.refundModal.accountNumber')}
                  aria-label={t('payments.refundModal.accountNumber')}
                  {...register('refundAccount.accountNumber')}
                />
                <Input
                  placeholder={t('payments.refundModal.holderName')}
                  aria-label={t('payments.refundModal.holderName')}
                  {...register('refundAccount.holderName')}
                />
              </div>
              {errors.refundAccount ? (
                <p className="mt-1 text-xs" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                  {t('payments.refundModal.refundAccountRequired')}
                </p>
              ) : null}
            </fieldset>
          ) : null}

          <div className="flex flex-col gap-2">
            <span id="manual-refund-reason-label" className="text-sm text-[color:var(--content-muted)]">
              {t('payments.refundModal.reason')}
            </span>
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  options={REFUND_REASON_OPTIONS.map((r) => ({ value: r, label: t(`payments.refundReason.${r}`) }))}
                  orientation="horizontal"
                  aria-labelledby="manual-refund-reason-label"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col">
              <label htmlFor="manual-refund-processed-at" className="text-sm text-[color:var(--content-muted)]">
                {t('payments.manualRefund.processedAt', '실제 환불 일시 (선택)')}
              </label>
              <Input id="manual-refund-processed-at" type="datetime-local" {...register('processedAt')} />
              {errors.processedAt ? (
                <p className="mt-1 text-xs" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                  {t('payments.manualRefund.processedAtFuture', '실제 환불 일시는 미래일 수 없습니다.')}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col">
              <label htmlFor="manual-refund-detail" className="text-sm text-[color:var(--content-muted)]">
                {t('payments.refundModal.detail')}
              </label>
              <Input id="manual-refund-detail" maxLength={255} {...register('detail')} />
            </div>
          </div>

          {errorMessage ? (
            <p className="text-sm" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="md" onClick={onCancel} disabled={isSubmitting}>
              {t('payments.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="md"
              disabled={isSubmitting || remaining <= 0}
              data-action="manual-refund-confirm"
            >
              {isSubmitting
                ? t('payments.refundModal.submitting')
                : t('payments.manualRefund.confirm', '환불 기록')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
